import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { BookingResponseDto, BookingServiceResponseDto } from '../../types/booking';
import { BookingFormValues, hasValidationErrors, validateBookingForm } from '../../utils/bookingValidators';

export type BookingModalMode = 'create' | 'edit' | 'reschedule';

interface Props {
  isOpen: boolean;
  mode: BookingModalMode;
  booking: BookingResponseDto | null;
  services: BookingServiceResponseDto[];
  saving?: boolean;
  onClose: () => void;
  onSave: (values: BookingFormValues) => void;
}

const buildEmptyForm = (): BookingFormValues => ({
  bookingServiceId: '',
  customerFirstName: '',
  customerLastName: '',
  customerEmail: '',
  customerPhone: '',
  customerNotes: '',
  bookingDate: '',
  bookingTime: '',
});

export default function BookingModal({ isOpen, mode, booking, services, saving, onClose, onSave }: Props) {
  const [values, setValues] = useState<BookingFormValues>(buildEmptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(
        booking
          ? {
              bookingServiceId: booking.bookingServiceId,
              customerFirstName: booking.customerFirstName,
              customerLastName: booking.customerLastName,
              customerEmail: booking.customerEmail,
              customerPhone: booking.customerPhone ?? '',
              customerNotes: booking.customerNotes ?? '',
              bookingDate: booking.bookingDate.slice(0, 10),
              bookingTime: new Date(booking.bookingDate).toISOString().slice(11, 16),
            }
          : buildEmptyForm()
      );
      setErrors({});
      setIsDirty(false);
    }
  }, [isOpen, booking]);

  if (!isOpen) return null;

  const handleChange = (field: keyof BookingFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = mode === 'reschedule'
      ? validateBookingForm({ ...values, bookingServiceId: values.bookingServiceId || 'n/a', customerFirstName: 'x', customerLastName: 'x', customerEmail: 'a@a.com' })
      : validateBookingForm(values);
    setErrors(validationErrors);
    if (hasValidationErrors(validationErrors)) return;
    onSave(values);
  };

  const handleClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
    onClose();
  };

  const titles: Record<BookingModalMode, string> = {
    create: 'Create Booking',
    edit: 'Edit Booking',
    reschedule: 'Reschedule Booking',
  };

  return (
    <div className="fixed inset-0 bg-strivre-navy/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 text-left">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-4">
          <h2 className="text-lg font-bold text-strivre-navy">{titles[mode]}</h2>
          <button onClick={handleClose} className="rounded-lg p-2 hover:bg-strivre-softgrey" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode !== 'reschedule' && (
            <>
              <div>
                <label className="block text-sm font-medium text-strivre-darktext mb-1">Service</label>
                <select className="input-base" value={values.bookingServiceId} onChange={(e) => handleChange('bookingServiceId', e.target.value)}>
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {errors.bookingServiceId && <p className="mt-1 text-xs text-strivre-burgundy">{errors.bookingServiceId}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-strivre-darktext mb-1">First Name</label>
                  <input className="input-base" value={values.customerFirstName} onChange={(e) => handleChange('customerFirstName', e.target.value)} />
                  {errors.customerFirstName && <p className="mt-1 text-xs text-strivre-burgundy">{errors.customerFirstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-strivre-darktext mb-1">Last Name</label>
                  <input className="input-base" value={values.customerLastName} onChange={(e) => handleChange('customerLastName', e.target.value)} />
                  {errors.customerLastName && <p className="mt-1 text-xs text-strivre-burgundy">{errors.customerLastName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-strivre-darktext mb-1">Email</label>
                <input type="email" className="input-base" value={values.customerEmail} onChange={(e) => handleChange('customerEmail', e.target.value)} />
                {errors.customerEmail && <p className="mt-1 text-xs text-strivre-burgundy">{errors.customerEmail}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-strivre-darktext mb-1">Phone Number</label>
                <input className="input-base" value={values.customerPhone} onChange={(e) => handleChange('customerPhone', e.target.value)} />
                {errors.customerPhone && <p className="mt-1 text-xs text-strivre-burgundy">{errors.customerPhone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-strivre-darktext mb-1">Notes</label>
                <textarea className="input-base" rows={2} value={values.customerNotes} onChange={(e) => handleChange('customerNotes', e.target.value)} />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Date</label>
              <input type="date" className="input-base" value={values.bookingDate} onChange={(e) => handleChange('bookingDate', e.target.value)} />
              {errors.bookingDate && <p className="mt-1 text-xs text-strivre-burgundy">{errors.bookingDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-strivre-darktext mb-1">Time</label>
              <input type="time" className="input-base" value={values.bookingTime} onChange={(e) => handleChange('bookingTime', e.target.value)} />
              {errors.bookingTime && <p className="mt-1 text-xs text-strivre-burgundy">{errors.bookingTime}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={handleClose} className="btn-outline btn-md">Cancel</button>
            <button type="submit" disabled={saving} className="btn-md text-white bg-strivre-navy hover:bg-strivre-navy/90 rounded-lg px-4 py-2 font-medium disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

